import { TOUR_IDS } from "@bcc/components/tour/tour-ids";
import { CommonHeader } from "@bcc/components/common-header";
import { AddProductSelectionModal } from "@bcc/components/products/add-product-selection-modal";
import { ManualAddProductModal } from "@bcc/components/products/manual-add-product-modal";
import { ProductSyncModal } from "@bcc/components/products/product-sync-modal";
import { ProductsTable } from "@bcc/components/products/products-table/products-table";
import type { VariantWithProductData } from "@bcc/components/products/products-table/types";
import { ShopifyConnectModal } from "@bcc/components/products/shopify-connect-modal";
import { ShopifyImportModal } from "@bcc/components/products/shopify-import-modal";
import { useImportModalUrlState } from "@bcc/components/products/shopify-import-modal/use-import-modal-url-state";
import { SsBagSizeProductSearchModal } from "@bcc/components/products/ss-bag-size-product-search-modal";
import { SsRejectedProductsModal } from "@bcc/components/products/ss-rejected-products-modal";
import { SsSkuDraftsModal } from "@bcc/components/products/ss-sku-drafts-modal";
import { SsSkuOnboardingModal } from "@bcc/components/products/ss-sku-onboarding-modal";
import { SsSkuSelectionModal } from "@bcc/components/products/ss-sku-selection-modal";
import { filterLocalisedProductConfigGrinds } from "@bcc/components/shopify-import/utils/filter-localised-product-config-grinds";
import { Button } from "@bcc/components/ui/button";
import { Input } from "@bcc/components/ui/input";
import { useRBAC } from "@bcc/contexts/rbac-context";
import { useTaxRatesContext } from "@bcc/contexts/tax-rates-context";
import {
  homeRegionForVendor,
  parseBagSizeToKg,
  parseShipToCountries,
  resolveDeDomesticWholesalePrice,
} from "@bcc/lib/products/cross-border-tax";
import { parsePrice } from "@bcc/lib/utils";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import { useConfigState } from "@client-shared/providers/config";
import { useInstallationsState } from "@client-shared/providers/installations";
import { setLoading, useProductsState } from "@client-shared/providers/products";
import { useVendorContext } from "@client-shared/providers/vendor";
import type { ResolveWholesalePrice } from "@client-shared/utilities/productSelectModal";
import { setAllRemoteProductsToSalesforceProducts } from "@client-shared/utilities/productSelectModal";
import type { FormProduct } from "@shared/types";
import { Link, Plus, Search } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildIntensityComparisonCoffees, getVendorLocaleMetadata, setProductsViewTestId } from "./products-view.utils";

interface ProductsViewProps {
  variantData: VariantWithProductData[];
  isLoading?: boolean;
  error?: Error;
  testId?: string;
  handleReload?: () => void;
  allowPimVariantIdAsSkuCode?: boolean;
  /** Visible products for SS roaster "New Bag Size" search modal — usually
   * the filtered list with Draft/Rejected SF records removed. */
  existingProducts?: FormProduct[];
  /** Full set of existing product titles used for duplicate-name validation
   * in SsSkuOnboardingModal. Must include hidden Draft/Rejected SKUs so
   * roasters can't create a second SKU with the same name as a record still
   * going through onboarding. Defaults to titles from `existingProducts`
   * when omitted. */
  existingProductTitles?: string[];
  /** Rejected Salesforce records (pre-PIM) for the SS "Rejected Products" modal. */
  rejectedProducts?: FormProduct[];
  /** Every Salesforce record for this vendor, whatever its approval status.
   *  Feeds the provisional (blue) intensity markers — a coffee still in Draft
   *  or Submitted is exactly what "provisional" means, so the rejected-only
   *  subset is not a substitute. Only supplied on the ShipStation page, which
   *  fetches Salesforce itself rather than through ProductsProvider. */
  salesforceProducts?: FormProduct[];
}

export function ProductsView({
  isLoading,
  error,
  variantData,
  testId,
  handleReload,
  allowPimVariantIdAsSkuCode = false,
  existingProducts = [],
  existingProductTitles,
  rejectedProducts = [],
  salesforceProducts = [],
}: ProductsViewProps) {
  const { t } = useTranslation();
  const { featureName } = useRBAC();
  const isShipstationRoaster = useMemo(() => featureName === "SS", [featureName]);
  const [showSkuModal, setShowSkuModal] = useState(false);
  const [showSelectionPopup, setShowSelectionPopup] = useState(false);
  const [showBagSizeSearch, setShowBagSizeSearch] = useState(false);
  const [showRejectedModal, setShowRejectedModal] = useState(false);
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [selectedProductForBagSize, setSelectedProductForBagSize] = useState<FormProduct | undefined>();
  const [resubmitProduct, setResubmitProduct] = useState<FormProduct | undefined>();
  const [draftProduct, setDraftProduct] = useState<FormProduct | undefined>();
  const existingProductNames = React.useMemo(
    () =>
      existingProductTitles ??
      existingProducts.map((existingProduct) => existingProduct.title).filter((title): title is string => Boolean(title)),
    [existingProducts, existingProductTitles]
  );
  const [authState] = useSFDCAuthState();
  const [{ currentVendor, currencyObject }] = useVendorContext();
  const ratesByRegion = useTaxRatesContext();
  // DE-domestic products must send the Kaffeesteuer-excluded wholesale price
  // (netto + VAT), same rule as the SS onboarding mapper — kept here as an
  // injected callback since `client-shared` must not import bcc's tax module.
  const resolveWholesalePrice: ResolveWholesalePrice = React.useCallback(
    (product, variant) => {
      const homeRegion = homeRegionForVendor(currentVendor?.countryCode, currentVendor?.region);
      const isDeDomestic = homeRegion === "DE" && parseShipToCountries(product.ship_to).length <= 1;
      if (!isDeDomestic) return variant.wholesalePrice;
      return resolveDeDomesticWholesalePrice(
        parsePrice(variant.price),
        parsePrice(variant.wholesalePrice),
        parseBagSizeToKg(variant.bagSize),
        ratesByRegion
      );
    },
    [currentVendor, ratesByRegion]
  );
  const [{ installations }] = useInstallationsState();
  const [productsState, productsDispatch, productsProviderMethods] = useProductsState();
  const { remoteFormProductsWithImported, skuImportDrafts, loading } = productsState;

  // Comparison markers for the intensity scale. PIM and Salesforce are the
  // approved and provisional sides of the same score: PIM only carries
  // WEB_INTENSITY_* once the beans manager has signed a score off, while
  // Salesforce holds what the roaster submitted at import time.
  //
  // Both roaster types are covered. Standard roasters get their catalog from
  // ProductsProvider; ShipStation roasters fetch PIM and Salesforce through the
  // page's own useQuery and receive them here as `existingProducts` (PIM) and
  // `salesforceProducts` (the complete Salesforce result, not the rejected-only
  // subset), so without the fallbacks the ShipStation flow would always report
  // "no other scored coffees".
  //
  // Built here and threaded down as a prop rather than read inside ProductForm,
  // which also runs in integrated mode where a provider dependency would be
  // hidden coupling.
  const comparisonCoffees = React.useMemo(
    () =>
      buildIntensityComparisonCoffees(
        [...(productsState.localParentChildProducts ?? []), ...existingProducts],
        [...(productsState.serviceProducts ?? []), ...salesforceProducts]
      ),
    [productsState.localParentChildProducts, productsState.serviceProducts, existingProducts, salesforceProducts]
  );
  const [{ config }] = useConfigState();
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [showManualAddModal, setShowManualAddModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  // Import modal open/step are mirrored in the URL (?select=true / ?import=true&products=...)
  // so a refresh or shared link restores the roaster to where they were (§11).
  // Selected product ids are only encoded once the flow reaches configure/review — not on the Select step.
  const importModalUrl = useImportModalUrlState();
  const [showProductSyncModal, setShowProductSyncModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const activeInstallation = React.useMemo(() => {
    if (!installations || installations.size === 0) return null;
    for (const [, inst] of installations) {
      if (inst.state === "ACTIVE") return inst;
    }
    return null;
  }, [installations]);

  React.useEffect(() => {
    if (!activeInstallation || !productsProviderMethods?.handleInstallationId) {
      return;
    }
    productsProviderMethods.handleInstallationId(activeInstallation.id);
  }, [activeInstallation, productsProviderMethods]);

  // ShipStation roasters have no Shopify installation, so the products
  // provider's skuImportDrafts fetch would otherwise stay gated forever
  // waiting on one. This lets it proceed scoped by vendorKey alone.
  React.useEffect(() => {
    if (!isShipstationRoaster) return;
    productsProviderMethods?.handleEnableVendorScopedDrafts?.();
  }, [isShipstationRoaster, productsProviderMethods]);

  const localizedProductConfig = React.useMemo(
    () =>
      filterLocalisedProductConfigGrinds(
        config.selectedLocalisedProductConfig ?? config.defaultRegionLocalisedProductConfig,
        config.productConfig
      ),
    [config.defaultRegionLocalisedProductConfig, config.selectedLocalisedProductConfig, config.productConfig]
  );

  const importedVariantIds = React.useMemo(() => {
    return (
      productsState.remoteFormProducts
        ?.flatMap((product) => {
          return product.variants?.filter((variant) => variant.isImportedLocalVariant).map((variant) => variant.id);
        })
        .filter((variantId): variantId is string => Boolean(variantId)) ?? []
    );
  }, [productsState.remoteFormProducts]);

  // Stay "loading" until the products provider has finished its initial
  // fetch AND materialized the form-product list. `loading.remoteProducts`
  // flips false as soon as the raw remote products land, but
  // `remoteFormProductsWithImported` is derived in a later effect (after the
  // filtered/config pipeline settles). Without the `=== undefined` guard
  // there's a window where isLoading is false but `products` is still []
  // — a refresh on a deep link (?import=true&products=…) would then resolve
  // zero products and prematurely fall back to the select step (§11).
  const isImportModalLoading = React.useMemo(
    () => loading.remoteProducts || !productsState.initialized || remoteFormProductsWithImported === undefined,
    [loading.remoteProducts, productsState.initialized, remoteFormProductsWithImported]
  );

  const canOpenProductSyncModal = Boolean(currentVendor) && Boolean(activeInstallation);

  const handleMapProducts = () => {
    if (!canOpenProductSyncModal) {
      return;
    }
    setShowProductSyncModal(true);
  };

  const handleAddProduct = () => {
    if (activeInstallation) {
      importModalUrl.openSelect();
    } else {
      setShowSelectionModal(true);
    }
  };

  const handleSelectManual = () => {
    setShowSelectionModal(false);
    setShowManualAddModal(true);
  };

  const handleSelectShopify = () => {
    setShowSelectionModal(false);
    if (activeInstallation) {
      importModalUrl.openSelect();
    } else {
      setShowConnectModal(true);
    }
  };

  const handleConnected = () => {
    importModalUrl.openSelect();
  };

  // Opens the SS onboarding modal in resubmit mode for a product picked from
  // the "Rejected Products" modal (which already hands us the full record).
  const handleResubmitRejected = React.useCallback((product: FormProduct) => {
    setShowRejectedModal(false);
    setSelectedProductForBagSize(undefined);
    setDraftProduct(undefined);
    setResubmitProduct(product);
    setShowSkuModal(true);
  }, []);

  // Opens the SS onboarding modal prefilled from a saved draft, picked from
  // the "View Draft / Last Saved" modal.
  const handleResumeDraft = React.useCallback((draft: FormProduct) => {
    setShowDraftsModal(false);
    setSelectedProductForBagSize(undefined);
    setResubmitProduct(undefined);
    setDraftProduct(draft);
    setShowSkuModal(true);
  }, []);

  const handleDraftChange = React.useCallback(
    (product: Parameters<NonNullable<typeof productsProviderMethods>["handlePostSkuImportDraft"]>[0]) => {
      void productsProviderMethods?.handlePostSkuImportDraft(product);
    },
    [productsProviderMethods]
  );

  const handleDraftPublished = React.useCallback(
    (products: Parameters<NonNullable<typeof productsProviderMethods>["handleUpdateSkuImportDrafts"]>[0]) => {
      void productsProviderMethods?.handleUpdateSkuImportDrafts(products, "PUBLISHED");
    },
    [productsProviderMethods]
  );

  // SsSkuOnboardingModal reports one product at a time (not the array shape
  // ShopifyImportModal's multi-product review uses) — wrap the same provider
  // methods that back handleDraftChange/handleDraftPublished above.
  const handleSsSaveDraft = React.useCallback(
    (product: FormProduct) => productsProviderMethods?.handlePostSkuImportDraft(product),
    [productsProviderMethods]
  );

  const handleSsDraftPublished = React.useCallback(
    (product: FormProduct) => {
      void productsProviderMethods?.handleUpdateSkuImportDrafts([product], "PUBLISHED");
    },
    [productsProviderMethods]
  );

  const handleDeleteDraft = React.useCallback(
    async (draft: FormProduct): Promise<boolean> => {
      if (!productsProviderMethods) return false;
      return productsProviderMethods.handleUpdateSkuImportDrafts([draft], "DISCARDED");
    },
    [productsProviderMethods]
  );

  const handleProductAdded = React.useCallback(() => {
    productsDispatch(setLoading("serviceProducts", true));
    productsDispatch(setLoading("connections", true));
    handleReload?.();
  }, [handleReload, productsDispatch]);

  const handleImportComplete = React.useCallback(
    async (products: FormProduct[]) => {
      const handlePostProducts = productsProviderMethods?.handlePostProducts;
      const sfdcAuthToken = authState.sfdcAuthToken;
      const userId = currentVendor?.userId;
      const vendorRegion = currentVendor?.region;
      const currencyIso = currencyObject?.iso;
      const installationId = activeInstallation?.id;

      if (!handlePostProducts || !sfdcAuthToken || !userId || !vendorRegion || !currencyIso || !installationId) {
        throw new Error("Unable to submit import: missing required authentication or configuration. Please refresh and try again.");
      }

      const { region: resolvedRegion, locales: regionLocales } = getVendorLocaleMetadata({
        region: vendorRegion,
        countryCode: currentVendor?.countryCode,
      });

      const mappedProducts = await setAllRemoteProductsToSalesforceProducts(
        products,
        userId,
        currencyIso,
        resolvedRegion,
        installationId,
        regionLocales,
        resolveWholesalePrice
      );

      await handlePostProducts("salesforce", sfdcAuthToken, mappedProducts);
    },
    [productsProviderMethods, authState.sfdcAuthToken, currentVendor, currencyObject, activeInstallation, resolveWholesalePrice]
  );

  // Runs after the modal's success pane dismisses so the products table
  // does not reload mid-flow (which would unmount the modal and hide the
  // success confirmation from the user).
  const handleImportFinished = React.useCallback(() => {
    handleProductAdded();
  }, [handleProductAdded]);

  function renderHeaderActions() {
    // While the page is loading we show a disabled Add button regardless of
    // roaster type — matches the pre-existing loading UX.
    if (isLoading) {
      return (
        <Button disabled data-testid={setProductsViewTestId("add-product-button")}>
          <Plus className="mr-2 h-4 w-4" />
          {t("views.products.addProduct")}
        </Button>
      );
    }

    if (isShipstationRoaster) {
      return (
        <Button
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={() => setShowSelectionPopup(true)}
          data-testid={setProductsViewTestId("add-sku-button")}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("ssRoaster.products.addSku")}
        </Button>
      );
    }

    return (
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          className="bg-muted text-foreground hover:bg-muted/90"
          onClick={handleMapProducts}
          disabled={!canOpenProductSyncModal}
          data-testid={setProductsViewTestId("map-products-button")}
        >
          <Link className="mr-2 h-4 w-4" />
          {t("onboarding.mapProducts.title", {
            defaultValue: "Map products",
          })}
        </Button>
        <Button
          className="bg-primary text-primary-foreground hover:bg-primary/90"
          onClick={handleAddProduct}
          data-testid={setProductsViewTestId("add-product-button")}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("views.products.addProduct")}
        </Button>
      </div>
    );
  }

  return (
    <div data-testid={testId ?? setProductsViewTestId("container")} className="space-y-6">
      <CommonHeader
        title={t("views.products.title")}
        description={t("views.products.description")}
        actions={renderHeaderActions()}
      ></CommonHeader>

      {error && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{t("views.productsTable.error")}</div>}

      <div className="relative" data-tour-id={TOUR_IDS.products.searchFilters}>
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-9"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={t("views.products.searchPlaceholder")}
          data-testid={setProductsViewTestId("search")}
        />
      </div>

      <div data-tour-id={TOUR_IDS.products.table}>
        <ProductsTable
          data={variantData}
          isLoading={isLoading}
          globalFilter={searchValue}
          handleReload={handleReload}
          allowPimVariantIdAsSkuCode={allowPimVariantIdAsSkuCode}
          isShipstationRoaster={isShipstationRoaster}
        />
      </div>

      <AddProductSelectionModal
        open={showSelectionModal}
        onOpenChange={setShowSelectionModal}
        onSelectManual={handleSelectManual}
        onSelectShopify={handleSelectShopify}
      />

      <ManualAddProductModal open={showManualAddModal} onOpenChange={setShowManualAddModal} onProductAdded={handleProductAdded} />

      <ShopifyConnectModal open={showConnectModal} onOpenChange={setShowConnectModal} onConnected={handleConnected} />

      <ShopifyImportModal
        open={importModalUrl.isOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) importModalUrl.close();
        }}
        initialStep={importModalUrl.initialStep}
        initialSelectedProductIds={importModalUrl.initialSelectedProductIds}
        onUrlStateChange={importModalUrl.syncFromModal}
        onImportComplete={handleImportComplete}
        onImportFinished={handleImportFinished}
        products={remoteFormProductsWithImported ?? []}
        draftProducts={skuImportDrafts}
        onDraftChange={handleDraftChange}
        onDraftPublished={handleDraftPublished}
        onDeleteDraft={handleDeleteDraft}
        isLoading={isImportModalLoading}
        serviceId={activeInstallation?.serviceId}
        existingProductTitles={existingProductNames}
        comparisonCoffees={comparisonCoffees}
        productConfig={config.productConfig}
        localisedProductConfig={localizedProductConfig}
        regionLargeBagSize={config.regionLargeBagSize}
        roasterNoteMaxLength={config.roasterNoteMaxLength}
        productDescriptionMaxLength={config.productDescriptionMaxLength}
        titleMaxLength={config.titleMaxLength}
        shortTermMinWeeklyInventory={config.inventoryConfig?.shortTermMinWeeklyInventory}
        shortTermMaxWeeklyInventory={config.inventoryConfig?.shortTermMaxWeeklyInventory}
        longTermMinWeeklyInventory={config.inventoryConfig?.longTermMinWeeklyInventory}
        longTermMaxWeeklyInventory={config.inventoryConfig?.longTermMaxWeeklyInventory}
        marginFloorPercent={config.marginConfig?.marginPercentage}
        onRemoteProductSearch={(params) => productsProviderMethods?.handleSearchRemoteProducts?.(params ?? undefined)}
      />

      {currentVendor && activeInstallation && (
        <ProductSyncModal
          expanded={showProductSyncModal}
          handleClose={() => setShowProductSyncModal(false)}
          handleReload={() => setShowProductSyncModal(false)}
          vendor={currentVendor}
          installation={activeInstallation}
          localProducts={productsState.localProducts ?? []}
          remoteProducts={productsState.remoteProducts ?? []}
          connections={productsState.connections ?? []}
          remoteFormProducts={productsState.remoteFormProducts ?? []}
          importedVariantIds={importedVariantIds}
          localizedProductConfig={localizedProductConfig}
        />
      )}

      {isShipstationRoaster && (
        <>
          <SsSkuSelectionModal
            open={showSelectionPopup}
            onOpenChange={setShowSelectionPopup}
            onSelectNewSku={() => {
              setShowSelectionPopup(false);
              setSelectedProductForBagSize(undefined);
              setResubmitProduct(undefined);
              setDraftProduct(undefined);
              setShowSkuModal(true);
            }}
            onSelectNewBagSize={() => {
              setShowSelectionPopup(false);
              setShowBagSizeSearch(true);
            }}
            onSelectRejectedProducts={() => {
              setShowSelectionPopup(false);
              setShowRejectedModal(true);
            }}
            onSelectDrafts={() => {
              setShowSelectionPopup(false);
              setShowDraftsModal(true);
            }}
          />

          <SsBagSizeProductSearchModal
            open={showBagSizeSearch}
            onOpenChange={setShowBagSizeSearch}
            products={existingProducts}
            onProductSelected={(product) => {
              setShowBagSizeSearch(false);
              setSelectedProductForBagSize(product);
              setResubmitProduct(undefined);
              setDraftProduct(undefined);
              setShowSkuModal(true);
            }}
          />

          <SsRejectedProductsModal
            open={showRejectedModal}
            onOpenChange={setShowRejectedModal}
            products={rejectedProducts}
            onResubmit={handleResubmitRejected}
          />

          <SsSkuDraftsModal
            open={showDraftsModal}
            onOpenChange={setShowDraftsModal}
            drafts={skuImportDrafts ?? []}
            onResume={handleResumeDraft}
            onDelete={handleDeleteDraft}
          />

          <SsSkuOnboardingModal
            comparisonCoffees={comparisonCoffees}
            open={showSkuModal}
            onOpenChange={setShowSkuModal}
            existingProduct={selectedProductForBagSize}
            existingProductNames={existingProductNames}
            resubmitProduct={resubmitProduct}
            draftProduct={draftProduct}
            onSaveDraft={handleSsSaveDraft}
            onDraftPublished={handleSsDraftPublished}
            onSubmitted={() => {
              setShowSkuModal(false);
              setSelectedProductForBagSize(undefined);
              setResubmitProduct(undefined);
              setDraftProduct(undefined);
              handleProductAdded();
            }}
            productConfig={config.productConfig}
            localisedProductConfig={localizedProductConfig}
            regionLargeBagSize={config.regionLargeBagSize}
            roasterNoteMaxLength={config.roasterNoteMaxLength}
            productDescriptionMaxLength={config.productDescriptionMaxLength}
            titleMaxLength={config.titleMaxLength}
            shortTermMinWeeklyInventory={config.inventoryConfig?.shortTermMinWeeklyInventory}
            shortTermMaxWeeklyInventory={config.inventoryConfig?.shortTermMaxWeeklyInventory}
            longTermMinWeeklyInventory={config.inventoryConfig?.longTermMinWeeklyInventory}
            longTermMaxWeeklyInventory={config.inventoryConfig?.longTermMaxWeeklyInventory}
            marginFloorPercent={config.marginConfig?.marginPercentage}
          />
        </>
      )}
    </div>
  );
}
